import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail, AUTH_LOGOUT_EVENT } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null);
  // Sync flag read by the api interceptor so the master account never hits shop APIs.
  const setUser = useCallback((u) => {
    if (typeof window !== "undefined") window.__wcPlatformLocked = !!(u && u.is_platform_admin && !u.is_impersonating);
    setUserState(u);
  }, []);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      // /auth/me/full includes the RBAC permission map so we only need one
      // round-trip on load. Falls back to /auth/me if the extended endpoint
      // isn't deployed yet (backwards compat with older backends).
      let data;
      try {
        ({ data } = await api.get("/auth/me/full"));
      } catch (_err) {
        ({ data } = await api.get("/auth/me"));
      }
      setUser(data);
      // Cache the shop's enabled-services list on window so job-type
      // and installation-type dropdowns filter correctly across every
      // page without prop-drilling. Best-effort — a failed fetch just
      // leaves the master list showing (safe default).
      try {
        const { data: es } = await api.get("/settings/enabled-services");
        if (typeof window !== "undefined") window.__enabledServices = es;
      } catch { /* leave master list as-is */ }
      // Job-workflow toggles — currently just the wrapping-exit gate.
      // Cache on window so `StatusPipeline` can gate without prop-drill.
      try {
        const { data: jw } = await api.get("/settings/job-workflow");
        if (typeof window !== "undefined") window.__jobWorkflow = jw;
      } catch { /* default = gate on */ }
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMe();
  }, [fetchMe]);

  // Global 401 handler in api.js dispatches this event when a protected
  // request comes back unauthenticated. Flip user->false so ProtectedRoute
  // does a soft <Navigate> to /login without a hard page reload (which was
  // wiping the sessionNotice banner mid-render).
  useEffect(() => {
    const onLogout = () => {
      localStorage.removeItem("wc_token");
      setUser(false);
    };
    window.addEventListener(AUTH_LOGOUT_EVENT, onLogout);
    return () => window.removeEventListener(AUTH_LOGOUT_EVENT, onLogout);
  }, []);

  const login = async (email, password) => {
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data?.token) localStorage.setItem("wc_token", data.token);
      // Seed with the lean login payload first so ProtectedRoute unblocks
      // immediately, then hydrate the full user (which includes the RBAC
      // permission map) so `usePermission()` returns correct values on the
      // very next render without needing a page reload.
      setUser(data.user);
      await fetchMe();
      return { ok: true };
    } catch (e) {
      return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
    }
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* ignore */
    }
    localStorage.removeItem("wc_token");
    setUser(false);
  };

  // Hydrate the auth state from an externally issued token (e.g. invite
  // acceptance flow). Mirrors `login()` but skips the credentials check.
  const setSession = async (token, userPayload) => {
    if (token) localStorage.setItem("wc_token", token);
    if (userPayload) setUser(userPayload);
    await fetchMe();
    return { ok: true };
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, setSession, refetch: fetchMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

/**
 * Handy hook for gating UI on a specific RBAC permission key.
 *   const canSeePricing = usePermission("view_pricing");
 * Admin role always passes so the app never locks itself out.
 */
export function usePermission(key) {
  const { user } = useAuth();
  if (!user) return false;
  if (user.role === "admin") return true;
  const perms = user.permissions || {};
  return !!perms[key];
}
